import axios from 'axios';

// --- API base URL: the CRA config boundary ---------------------------------
// `REACT_APP_*` env vars are inlined by Create React App at BUILD time (baked
// into the static bundle via webpack's DefinePlugin) — they are NOT read at
// runtime. Changing the backend URL therefore requires a rebuild, not just a
// new environment value on the serving host. The default fallback must match
// the Flask backend port (7003). The same
// `process.env.REACT_APP_API_URL || 'http://localhost:7003/api'` expression is
// duplicated in auth.service.ts, App.tsx and Sidebar.tsx — keep them in sync
// (or centralize) whenever this boundary changes.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7003/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for authentication
});

export interface Project {
  project_key: string;
  name: string;
  description: string;
  config?: ConfigItem[];
  engine_version: string;
}

export interface ConfigItem {
  key: string;
  value: any;
  description: string;
  isVisible: boolean;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface ConversationHistory {
  id: number;
  user_id: string;
  session_id: string;
  project_key: string;
  query: string;
  response: string;
  created_at: string;
  updated_at: string;
  metadata: {
    agents_used?: number;
    confidence?: number;
    execution_mode?: string;
    personality_mode?: string;
    processing_time?: number;
  };
}

export interface ConversationHistoryResponse {
  success: boolean;
  data: {
    conversations: ConversationHistory[];
    total_count: number;
    has_more: boolean;
    pagination: {
      limit: number;
      returned: number;
    };
  };
  message: string;
}

// Multi-turn conversation interfaces
export interface MultiTurnConversation {
  id: string;
  user_id: string;
  session_id: string;
  project_key: string;
  title: string;
  message_count: number;
  metadata: any;
  created_at: string;
  updated_at: string;
  last_message_at: string;
}

export interface ConversationMessageItem {
  id: number;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata: any;
  created_at: string;
  sequence_number: number;
  timestamp: number;
}

export interface QuestionRequest {
  selectedProject: string;
  question: string;
  options: QuestionOptions;
  session_id?: string;
  // user_id removed - backend now uses authenticated user from session
}

export interface QuestionOptions {
  mode?: 'basic' | 'extended';
  personality_mode?: 'simple' | 'professional' | 'teacher';
  enable_query_rewriting?: boolean;
  enable_confidence_scoring?: boolean;
  enable_abstention?: boolean;
  confidence_threshold?: number;
  search_strategy?: 'hybrid' | 'semantic' | 'keyword';
  top_k_agents?: number;
  timeout?: number;
}

export interface AgentResponse {
  agent_id: string;
  agent_type: string;
  agent_name: string;
  response: {
    answer: string;
    confidence: number;
    sources: string[];
  };
  execution_time: number;
  success: boolean;
  error?: string;
  relevance_score?: number;
}

export interface OrchestratorMetadata {
  total_execution_time: number;
  query_classification: {
    complexity: 'SIMPLE' | 'COMPLEX' | 'DOMAIN_SPECIFIC';
    confidence: number;
    reasoning: string;
  };
  execution_summary: {
    total_agents_queried: number;
    successful_agents: number;
    failed_agents: number;
    average_confidence?: number;
  };
  recovery_attempts: number;
}

export interface QuestionResponse {
  success: boolean;
  query: string;
  answer: string;
  confidence?: number;
  sources?: string[];
  agents_used?: number;
  execution_mode?: 'basic' | 'extended';
  personality_mode?: 'simple' | 'professional' | 'teacher';
  orchestrator_metadata?: OrchestratorMetadata;
  conversation_id?: string;
  agent_responses?: AgentResponse[];
  processing_metadata?: {
    endpoint: string;
    processing_time_seconds: number;
    intelligent_features_enabled?: boolean;
    extended_thinking_enabled?: boolean;
  };
  error?: string;
  error_type?: 'domain_refusal' | 'no_agents' | 'orchestration_failure';
  // Clarification fields
  needs_clarification?: boolean;
  clarification_info?: {
    message?: string;
    missing_info?: string[];
    suggestions?: string[];
    confidence?: number;
  };
}

export const apiService = {
  // Get all projects
  async getProjects(): Promise<Project[]> {
    try {
      const response = await api.get('/ie/projects', {
        headers: {
          'X-User-ID': 'default_user'  // Use default user for now
        }
      });
      // Backend returns nested structure: {data: {projects: []}}
      return response.data.data?.projects || response.data.projects || response.data || [];
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  // Ask a question to a specific project using V2 API
  async askQuestion(request: QuestionRequest): Promise<QuestionResponse> {
    try {
      const v2Request = {
        question: request.question,
        project_key: request.selectedProject,
        session_id: request.session_id,
        // user_id removed - backend now uses authenticated user from session
        options: {
          mode: request.options.mode || 'basic',
          personality_mode: request.options.personality_mode || 'professional',
          enable_query_rewriting: request.options.enable_query_rewriting ?? true,
          enable_confidence_scoring: request.options.enable_confidence_scoring ?? true,
          enable_abstention: request.options.enable_abstention ?? true,
          confidence_threshold: request.options.confidence_threshold ?? 0.30,
          search_strategy: request.options.search_strategy || 'hybrid',
          top_k_agents: request.options.top_k_agents || 5,
          timeout: request.options.timeout || 60
        }
      };

      const response = await api.post('/v2/ask', v2Request);
      let rawData = response.data;
      
      // Backend sometimes returns [response, statusCode] tuple - extract the response object
      if (Array.isArray(rawData)) {
        console.log('[API] Response is array, extracting first element');
        rawData = rawData[0];
      }
      
      // Backend wraps response in { success, message, data: {...} }
      // Extract the nested data object, fallback to rawData for backward compatibility
      const data = rawData.data || rawData;

      // Debug logging for response structure
      console.log('[API] Extracted data.answer:', data.answer ? data.answer.substring(0, 200) : 'null/undefined');

      // Log conversation_id for debugging
      if (data.conversation_id) {
        console.log(`[API] Received conversation_id from backend: ${data.conversation_id}`);
      }

      // Log clarification response for debugging
      if (data.needs_clarification) {
        console.log('[API] Clarification needed:', data.clarification_info);
        console.log('[API] Clarification answer:', data.answer);
      }

      return {
        success: rawData.success ?? data.success,
        query: request.question,
        answer: data.answer,
        confidence: data.confidence,
        sources: data.sources || [],
        agents_used: data.agents_used,
        execution_mode: data.execution_mode,
        personality_mode: data.personality_mode,
        orchestrator_metadata: data.orchestrator_metadata,
        agent_responses: data.agent_responses,
        processing_metadata: data.processing_metadata,
        error: data.error,
        error_type: data.error_type,
        conversation_id: data.conversation_id,
        needs_clarification: data.needs_clarification,
        clarification_info: data.clarification_info
      };
    } catch (error) {
      // Handle 422 responses specially - they contain user-friendly abstention messages
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const data = error.response.data;
        console.log('[API] Received 422 (domain refusal/abstention):', data.error_type);

        // Return the response data - it contains a user-friendly message in the 'answer' field
        return {
          success: data.success || false,
          query: request.question,
          answer: data.answer || data.error || 'Unable to process this query',
          confidence: data.confidence || 0,
          sources: data.sources || [],
          agents_used: data.agents_used || 0,
          execution_mode: data.execution_mode,
          personality_mode: data.personality_mode,
          orchestrator_metadata: data.orchestrator_metadata,
          agent_responses: data.agent_responses,
          processing_metadata: data.processing_metadata,
          error: data.error,
          error_type: data.error_type,
          conversation_id: data.conversation_id
        };
      }

      console.error('Error asking question:', error);
      throw error;
    }
  },

  // Conversation History API methods
  async getConversationHistory(userId: string, projectKey: string, limit: number = 5): Promise<ConversationHistoryResponse> {
    try {
      const response = await api.get(`/v2/conversations/${userId}/history`, {
        params: {
          project_key: projectKey,
          limit: limit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      throw error;
    }
  },

  async searchConversationHistory(userId: string, projectKey: string, query: string, topK: number = 5) {
    try {
      const response = await api.get(`/v2/conversations/${userId}/search`, {
        params: {
          project_key: projectKey,
          query: query,
          top_k: topK
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching conversation history:', error);
      throw error;
    }
  },

  async getConversationContext(userId: string, projectKey: string) {
    try {
      const response = await api.get(`/v2/conversations/${userId}/context`, {
        params: {
          project_key: projectKey
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation context:', error);
      throw error;
    }
  },

  async cleanupConversations(userId: string, projectKey: string, keepRecent: number = 5, maxAgeDays: number = 30) {
    try {
      const response = await api.delete(`/v2/conversations/${userId}/cleanup`, {
        params: {
          project_key: projectKey,
          keep_recent: keepRecent,
          max_age_days: maxAgeDays
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error cleaning up conversations:', error);
      throw error;
    }
  },

  // Multi-turn conversation API methods
  async getConversationMessages(
    conversationId: string,
    projectKey: string
  ): Promise<ConversationMessageItem[]> {
    try {
      const response = await api.get(
        `/v2/multiturn/conversations/${conversationId}/messages`,
        { params: { project_key: projectKey } }
      );
      return response.data.data.messages;
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      throw error;
    }
  },

  async getMultiTurnConversations(
    projectKey: string,
    limit: number = 10
  ): Promise<MultiTurnConversation[]> {
    try {
      const response = await api.get(
        `/v2/multiturn/conversations`,
        { 
          params: { 
            project_key: projectKey,
            limit: limit
          } 
        }
      );
      return response.data.data.conversations;
    } catch (error) {
      console.error('Error fetching multi-turn conversations:', error);
      throw error;
    }
  },
};

export default apiService;